/**
 * Transfer Queue Context — GenesisBox
 * Manages upload/download task queue.
 */

import { createContext, useState, useCallback, type ReactNode } from 'react';
import type { TransferTask } from '@/types/transfer.types';

interface TransferQueueContextType {
  tasks: TransferTask[];
  addTask: (task: TransferTask) => void;
  updateTask: (id: string, updates: Partial<TransferTask>) => void;
  removeTask: (id: string) => void;
  clearCompleted: () => void;
}

const TransferQueueContext = createContext<TransferQueueContextType | undefined>(undefined);

export function TransferQueueProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<TransferTask[]>([]);

  const addTask = useCallback((task: TransferTask) => {
    setTasks((prev) => [...prev, task]);
  }, []);

  const updateTask = useCallback((id: string, updates: Partial<TransferTask>) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
    );
  }, []);

  const removeTask = useCallback((id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setTasks((prev) => prev.filter((t) => t.status !== 'completed'));
  }, []);

  return (
    <TransferQueueContext.Provider
      value={{ tasks, addTask, updateTask, removeTask, clearCompleted }}
    >
      {children}
    </TransferQueueContext.Provider>
  );
}

export { TransferQueueContext };
